import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  LinearProgress,
  Skeleton,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  TrendingUp,
  Handshake,
  PinDrop,
  Image,
  Mic,
  QuestionAnswer,
  AccessTime,
  Login,
} from '@mui/icons-material';
import { fetchTrendsData } from '../utils/csDashboardHelperFunctions';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

interface CSTrendsProps {
  organization: string;
  startDate: Date | null;
  endDate: Date | null;
  teams: string;
  individuals: string;
  granularity: 'daily' | 'weekly' | 'monthly';
}

// Chart Skeleton Component
const ChartSkeleton = ({
  title,
  icon,
  color,
}: {
  title: string;
  icon: React.ReactNode;
  color: string;
}) => (
  <Card elevation={2}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Box sx={{ color: `${color}.main` }}>{icon}</Box>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        <Skeleton variant="text" width="80%" />
      </Typography>
      <Box
        height={300}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <Skeleton
            variant="rectangular"
            width="90%"
            height="200"
            sx={{
              borderRadius: 2,
              bgcolor: (theme) =>
                theme.custom?.background3 || theme.palette.grey[200],
            }}
          />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export default function CSTrends({
  organization,
  startDate,
  endDate,
  teams,
  individuals,
  granularity,
}: CSTrendsProps) {
  const [trendsData, setTrendsData] = useState<any>(null);
  const [labels, setLabels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentRequestIdRef = useRef<number>(0);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  // Fetch trends data when component mounts or dependencies change
  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate) {
        setTrendsData(null);
        setLabels([]);
        setLoading(false);
        return;
      }

      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller and increment request ID
      abortControllerRef.current = new AbortController();
      const requestId = ++currentRequestIdRef.current;
      setLoading(true);
      setError(false);

      try {
        const result = await fetchTrendsData(
          organization,
          startDate,
          endDate,
          abortControllerRef.current.signal,
          teams,
          individuals,
          granularity
        );

        // Only update state if this is still the current request
        if (requestId === currentRequestIdRef.current) {
          if (result) {
            setTrendsData(result.data);
            setLabels(result.labels);
          } else {
            setTrendsData(null);
            setLabels([]);
          }
          setLoading(false);
        }
      } catch (error) {
        // Only update state if this is still the current request
        if (requestId === currentRequestIdRef.current) {
          if ((error as Error).message !== 'Request aborted') {
            console.error('Error fetching trends data:', error);
            setError(true);
          }
          setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [organization, startDate, endDate, teams, individuals, granularity]);

  // Check if date range is appropriate for the selected granularity
  const isDateRangeAppropriate = () => {
    if (!startDate || !endDate) return false;

    const timeDiff = endDate.getTime() - startDate.getTime();
    const oneDayMs = 24 * 60 * 60 * 1000;
    const oneWeekMs = 7 * oneDayMs;
    const threeMonthsMs = 90 * oneDayMs;
    const oneYearMs = 365 * oneDayMs;

    switch (granularity) {
      case 'daily':
        return timeDiff <= oneWeekMs;
      case 'weekly':
        return timeDiff <= 5 * oneWeekMs; // Allow up to 5 weeks (35 days)
      case 'monthly':
        return timeDiff >= threeMonthsMs && timeDiff <= oneYearMs;
      default:
        return false;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <>
        <Box mb={3}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" mt={1}>
            Loading trends data...
          </Typography>
        </Box>
        {/* User Logins & Pins Created Trends Skeletons */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <ChartSkeleton
              title="User Logins Trend"
              icon={<Login />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartSkeleton
              title="Pins Created Trend"
              icon={<PinDrop />}
              color="secondary"
            />
          </Grid>
        </Grid>

        {/* Deals Created & Images Uploaded Trends Skeletons */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <ChartSkeleton
              title="Deals Created Trend"
              icon={<Handshake />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartSkeleton
              title="Images Uploaded Trend"
              icon={<Image />}
              color="info"
            />
          </Grid>
        </Grid>

        {/* Recordings Taken & Ask Paiv Questions Trends Skeletons */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <ChartSkeleton
              title="Recordings Taken Trend"
              icon={<Mic />}
              color="error"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartSkeleton
              title="Ask Paiv Questions Trend"
              icon={<QuestionAnswer />}
              color="secondary"
            />
          </Grid>
        </Grid>

        {/* Deal Stage Changes & Average Time in App Trends Skeletons */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <ChartSkeleton
              title="Deal Stage Changes Trend"
              icon={<TrendingUp />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartSkeleton
              title="Average Time in App Trend"
              icon={<AccessTime />}
              color="primary"
            />
          </Grid>
        </Grid>
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography variant="body1" fontWeight={500} gutterBottom>
          Error Loading Trends Data
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Failed to load trends data. Please try again later.
        </Typography>
      </Alert>
    );
  }

  if (!isDateRangeAppropriate()) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body1" fontWeight={500} gutterBottom>
          Date Range Adjustment Required
        </Typography>
        <Typography variant="body2" color="text.secondary">
          For {granularity} granularity, please adjust your date range:
          {granularity === 'daily' && ' Maximum 1 week'}
          {granularity === 'weekly' && ' Maximum 5 weeks'}
          {granularity === 'monthly' && ' Between 3 months and 1 year'}
        </Typography>
      </Alert>
    );
  }

  // If no data available, show skeleton charts instead of just a message
  if (!trendsData || !labels.length) {
    return (
      <>
        {/* Overview Section */}
        <Box mb={4}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Trends Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Track patterns and trends across your organization with{' '}
            {granularity} granularity
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              No trends data available yet.
            </Typography>
          </Alert>
        </Box>
      </>
    );
  }

  // Individual trend charts for each metric
  const userLoginsData = {
    labels,
    datasets: [
      {
        label: 'User Logins',
        data: trendsData.userLogins || [],
        borderColor: 'rgb(25, 118, 210)',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        tension: 0.3,
      },
    ],
  };

  const pinsCreatedData = {
    labels,
    datasets: [
      {
        label: 'Pins Created',
        data: trendsData.pinsCreated || [],
        borderColor: 'rgb(156, 39, 176)',
        backgroundColor: 'rgba(156, 39, 176, 0.1)',
        tension: 0.3,
      },
    ],
  };

  const dealsCreatedData = {
    labels,
    datasets: [
      {
        label: 'Deals Created',
        data: trendsData.dealsCreated || [],
        borderColor: 'rgb(46, 125, 50)',
        backgroundColor: 'rgba(46, 125, 50, 0.1)',
        tension: 0.3,
      },
    ],
  };

  const imagesUploadedData = {
    labels,
    datasets: [
      {
        label: 'Images Uploaded',
        data: trendsData.imagesUploaded || [],
        borderColor: 'rgb(245, 124, 0)',
        backgroundColor: 'rgba(245, 124, 0, 0.1)',
        tension: 0.3,
      },
    ],
  };

  const recordingsTakenData = {
    labels,
    datasets: [
      {
        label: 'Recordings Taken',
        data: trendsData.recordingsTaken || [],
        borderColor: 'rgb(244, 67, 54)',
        backgroundColor: 'rgba(244, 67, 54, 0.1)',
        tension: 0.3,
      },
    ],
  };

  const askPaivQuestionsData = {
    labels,
    datasets: [
      {
        label: 'Ask Paiv Questions',
        data: trendsData.askPaivQuestions || [],
        borderColor: 'rgb(156, 39, 176)',
        backgroundColor: 'rgba(156, 39, 176, 0.1)',
        tension: 0.3,
      },
    ],
  };

  const dealStageChangesData = {
    labels,
    datasets: [
      {
        label: 'Deal Stage Changes',
        data: trendsData.dealStageChanges || [],
        borderColor: 'rgb(46, 125, 50)',
        backgroundColor: 'rgba(46, 125, 50, 0.1)',
        tension: 0.3,
      },
    ],
  };

  const avgTimeInAppData = {
    labels,
    datasets: [
      {
        label: 'Average Time in App (minutes)',
        data: trendsData.avgTimeInApp || [],
        borderColor: 'rgb(245, 124, 0)',
        backgroundColor: 'rgba(245, 124, 0, 0.1)',
        tension: 0.3,
      },
    ],
  };

  return (
    <>
      {/* Overview Section */}
      <Box mb={4}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Trends Analysis
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track patterns and trends across your organization with {granularity}{' '}
          granularity
        </Typography>
      </Box>

      {/* User Logins & Pins Created Trends */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Login color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  User Logins Trend
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Track user login patterns over time
              </Typography>
              <Box height={300}>
                <Line data={userLoginsData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PinDrop color="secondary" />
                <Typography variant="h6" fontWeight={600}>
                  Pins Created Trend
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Monitor pin creation activity over time
              </Typography>
              <Box height={300}>
                <Line data={pinsCreatedData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Deals Created & Images Uploaded Trends */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Handshake color="success" />
                <Typography variant="h6" fontWeight={600}>
                  Deals Created Trend
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Track new deal creation patterns over time
              </Typography>
              <Box height={300}>
                <Line data={dealsCreatedData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Image color="info" />
                <Typography variant="h6" fontWeight={600}>
                  Images Uploaded Trend
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Monitor image upload activity over time
              </Typography>
              <Box height={300}>
                <Line data={imagesUploadedData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recordings Taken & Ask Paiv Questions Trends */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Mic color="error" />
                <Typography variant="h6" fontWeight={600}>
                  Recordings Taken Trend
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Track audio recording activity over time
              </Typography>
              <Box height={300}>
                <Line data={recordingsTakenData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <QuestionAnswer color="secondary" />
                <Typography variant="h6" fontWeight={600}>
                  Ask Paiv Questions Trend
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Monitor AI assistant usage patterns over time
              </Typography>
              <Box height={300}>
                <Line data={askPaivQuestionsData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Deal Stage Changes & Average Time in App Trends */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TrendingUp color="warning" />
                <Typography variant="h6" fontWeight={600}>
                  Deal Stage Changes Trend
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Track deal progression activity over time
              </Typography>
              <Box height={300}>
                <Line data={dealStageChangesData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AccessTime color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Average Time in App Trend
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Monitor user engagement through time spent in app
              </Typography>
              <Box height={300}>
                <Line data={avgTimeInAppData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
